// src/components/EmailVerification.jsx
import { useState, useEffect, useRef } from "react";
import LoadingSvg from "../Loading";
import { useAuth } from "../../auth/AuthContext"; // 👈 ajusta ruta
import { useNavigate } from "react-router-dom";

export default function EmailVerification({ email, onVerify }) {
  const { login } = useAuth();
  const navigate = useNavigate();

  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMsg, setResendMsg] = useState("");
  const [countdown, setCountdown] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    return () => clearInterval(timerRef.current);
  }, []);

  const handleVerify = async () => {
    setError("");

    if (code.length !== 6) {
      setError("Code must be 6 digits.");
      return;
    }

    setLoading(true);

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/auth/verify`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include", // ✅ cookies HttpOnly
          body: JSON.stringify({ email, code }),
        }
      );

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data?.error || "Incorrect code");
      }

      if (!data?.verified) {
        throw new Error("Invalid verification");
      }

      // callback opcional al padre
      onVerify?.();
    } catch (err) {
      console.error(err);
      setError(err.message || "Error verifying. Try again later.");
    } finally { 
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResendMsg("");
    setError("");
    setResendLoading(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/auth/resend-verification`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email }),
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || "Error resending code");
      setResendMsg("A new code has been sent to your email.");
      clearInterval(timerRef.current);
      setCountdown(60);
      timerRef.current = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (err) {
      setError(err.message || "Error resending. Try again later.");
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <>
      {loading && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <LoadingSvg size={400} pulse />
        </div>
      )}

      <div className="w-full min-h-screen bg-gradient-to-b from-emerald-100 via-amber-200 to-red-100 flex justify-center items-center p-4 md:p-8">
        <div className="bg-white/35 border border-white/40 backdrop-blur-md rounded-2xl shadow-[0_28px_70px_rgba(15,23,42,0.28)] w-full max-w-xl p-10 flex flex-col gap-6 text-center">
          <h2 className="text-3xl font-semibold">Verify your email</h2>

          <p className="text-gray-600">
            Enter the 6-digit code sent to
            <span className="font-semibold text-green-600"> {email}</span>
          </p>

          <input
            type="text"
            maxLength={6}
            value={code}
            onChange={(e) => {
              const onlyNumbers = e.target.value.replace(/\D/g, "");
              setCode(onlyNumbers);
            }}
            className="w-full bg-transparent text-center text-2xl tracking-[0.5em] py-3 border-b-2 border-gray-400 focus:border-green-600 outline-none"
          />

          <button
            onClick={handleVerify}
            disabled={loading}
            className={`w-full bg-green-500 hover:bg-green-600 text-white py-3 rounded-xl transition 
            ${loading ? "opacity-70 cursor-not-allowed" : ""}`}
          >
            {loading ? "Verifying..." : "Verify"}
          </button>

          {error && <p className="text-red-600 mt-2">{error}</p>}

          {resendMsg && <p className="text-green-600 mt-2">{resendMsg}</p>}

          <button
            type="button"
            onClick={handleResend}
            disabled={resendLoading || countdown > 0}
            className="text-sm text-gray-500 hover:text-green-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {resendLoading
              ? "Sending..."
              : countdown > 0
              ? `Resend code (${countdown}s)`
              : "Resend code"}
          </button>

          <button
            type="button"
            onClick={() => navigate("/")}
            className="text-sm text-gray-400 hover:text-gray-700 transition"
          >
            ← Back to Login
          </button>
        </div>
      </div>
    </>
  );
}
